// components/secure/pm/BusinessUnderwriting.js — commercial and industrial intake
// and underwriting.
//
// A different question from the residential one. There, the screen asks whether
// a household can pay and enforces the order the housing ordinances impose.
// Here it asks whether the covenant covers the rent, and if not, what stands
// behind it — so the shape is one underwriting pass, not a gated sequence.
//
// Things the panel does on purpose:
//
//   * Rent coverage is computed while it is typed. It is the ratio the whole
//     decision turns on, and an agent who watches EBITDA over annual rent
//     resolve to 1.2x understands the guaranty request that follows.
//
//   * A short covenant is never presented as a refusal. The server recommends a
//     guaranty, and the panel shows it as the next move.
//
//   * Guarantors are people. Each one whose personal credit report is pulled
//     carries the FCRA adverse action duty with them, so the tick is per person.
//
//   * The form re-opens on what the file holds (the server re-assesses the stored
//     row), and it locks once the application is decided: the decision stands on
//     the underwriting it was made on.
import React, { useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Pill, Input, Select, Textarea, Button, Notice, money } from './PMKit';

const ENTITY_TYPES = [
    ['llc', 'LLC'], ['corporation', 'Corporation'], ['partnership', 'Partnership'],
    ['sole_proprietor', 'Sole proprietor'], ['franchisee', 'Franchisee'],
    ['nonprofit', 'Non-profit'], ['other', 'Other'],
];
export const GUARANTY_LABEL = {
    none: 'No guaranty needed', personal: 'Personal guaranty', corporate: 'Corporate guaranty',
    letter_of_credit: 'Letter of credit', case_by_case: 'Decide case by case',
};
const FLAGS = ['handles_hazardous_materials', 'phase_i_esa_on_file', 'outdoor_storage_requested',
    'zoning_use_verified', 'principal_credit_pulled', 'bank_reference_checked'];

const Section = ({ title, hint, children }) => (
    <div className="border-t border-slate-100 pt-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
        {hint && <p className="mb-2 text-xs text-slate-500">{hint}</p>}
        <div className="mt-2">{children}</div>
    </div>
);

const Check = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-1">
        <input type="checkbox" checked={!!checked} onChange={onChange} />
        {label}
    </label>
);

// A repeating group of small records (guarantors, references).
const Rows = ({ rows, onChange, blank, render, addLabel, disabled }) => (
    <div className="space-y-2">
        {rows.map((r, i) => (
            <div key={i} className="rounded border border-slate-200 p-2">
                {render(r, (k, v) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: v } : x))))}
                {!disabled && (
                    <button type="button" className="text-xs text-rose-600 hover:underline"
                        onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>
                )}
            </div>
        ))}
        {!disabled && (
            <button type="button" className="text-xs text-indigo-600 hover:underline"
                onClick={() => onChange([...rows, { ...blank }])}>{addLabel}</button>
        )}
    </div>
);

const fromStored = (b, application) => {
    if (!b) {
        return { entity_type: 'llc', legal_name: application?.applicant_name || '',
                 guarantors: [], bank_references: [], trade_references: [] };
    }
    const f = {};
    for (const [k, v] of Object.entries(b)) {
        if (v === null || v === undefined) continue;
        if (FLAGS.includes(k)) f[k] = !!Number(v);
        else if (Array.isArray(v)) f[k] = v;
        else if (k === 'phase_i_esa_date') f[k] = String(v).slice(0, 10);
        else if (typeof v === 'number' || typeof v === 'string') f[k] = String(v).replace(/\.00$/, '');
    }
    f.guarantors = Array.isArray(b.guarantors) ? b.guarantors : [];
    f.bank_references = Array.isArray(b.bank_references) ? b.bank_references : [];
    f.trade_references = Array.isArray(b.trade_references) ? b.trade_references : [];
    return f;
};

export const UnderwritingResult = ({ result, environmental, criteriaVersion }) => (
    <div className="space-y-2 rounded border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
            <Pill tone={result.underwritten ? 'good' : 'warn'}>
                {result.underwritten ? 'Meets the criteria' : 'Short of the criteria'}
            </Pill>
            <Pill tone="slate">{GUARANTY_LABEL[result.guarantyRecommended] || result.guarantyRecommended}</Pill>
            {result.adverseActionRequired && <Pill tone="bad">FCRA notice owed if declined</Pill>}
            {criteriaVersion && <span className="text-xs text-slate-400">criteria {criteriaVersion}</span>}
        </div>
        {result.results?.coverage?.basis && <p className="text-xs text-slate-500">{result.results.coverage.basis}</p>}
        {result.failures?.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">
                {result.failures.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
        )}
        {environmental && !environmental.clear && (
            <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Open before signature</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {environmental.open.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
            </div>
        )}
        {environmental?.clear && <p className="text-sm text-emerald-800">Environmental and fit questions are answered.</p>}
    </div>
);

const BusinessUnderwriting = ({ applicationId, application, criteria, screening, business, assessed, onDone }) => {
    const [f, setF] = useState(() => fromStored(business, application));
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const apiClient = useApiClient();
    const industrial = application?.asset_class === 'industrial';
    const locked = screening?.stage === 'decided';

    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const check = (k) => (e) => setF({ ...f, [k]: e.target.checked });
    const setList = (k) => (rows) => setF({ ...f, [k]: rows });

    // The ratio, live. Nothing else on this screen matters as much.
    const ebitda = Number(f.ebitda) || 0;
    const annualRent = Number(f.annual_rent) || 0;
    const coverage = annualRent > 0 && f.ebitda !== undefined && f.ebitda !== ''
        ? +(ebitda / annualRent).toFixed(2) : null;
    const minCoverage = Number(criteria?.rent_coverage_ratio) || null;
    const coverageShort = coverage != null && minCoverage != null && coverage < minCoverage;

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const body = { ...f };
            for (const k of FLAGS) body[k] = !!f[k];
            body.guarantors = (f.guarantors || []).map((g) => ({ ...g, credit_pulled: !!g.credit_pulled }));
            const r = await apiClient.post(`/api/screening/applications/${applicationId}/underwrite`, body);
            setResult(r);
            onDone?.();
        } catch (e) { setErr(e); } finally { setBusy(false); }
    };

    const shown = result
        || (assessed?.underwriting ? { ...assessed.underwriting, environmental: assessed.environmental } : null);
    const refRow = (whoLabel) => (r, up) => (
        <div className="grid gap-x-2 sm:grid-cols-2">
            <Input label={whoLabel} value={r.name || ''} onChange={(e) => up('name', e.target.value)} />
            <Input label="Contact / phone" value={r.contact || ''} onChange={(e) => up('contact', e.target.value)} />
            <Input label="What they said (blank = not yet called)" className="sm:col-span-2"
                   value={r.result || ''} onChange={(e) => up('result', e.target.value)} />
        </div>
    );

    return (
        <div className="space-y-4">
            {err && <Notice tone="bad" title="Refused" message={err.message} problems={err.problems} />}

            <p className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Business tenancy. The housing rules do not apply here — no conditional-offer sequence, and no
                source-of-income arithmetic. If a personal credit report is pulled on an owner or guarantor, the
                FCRA adverse action duty still follows that individual.
            </p>
            {locked && (
                <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Decided — the underwriting below is what the decision was made on, and it is not edited after the fact.
                </p>
            )}

            <fieldset disabled={locked} className="space-y-4">
                <Section title="The entity">
                    <div className="grid gap-x-4 sm:grid-cols-3">
                        <Input label="Legal name" value={f.legal_name || ''} onChange={set('legal_name')} />
                        <Input label="Trading as" value={f.dba || ''} onChange={set('dba')} />
                        <Input label="Contact person" value={f.contact_name || ''} onChange={set('contact_name')} />
                        <Select label="Type" value={f.entity_type || 'llc'} onChange={set('entity_type')}
                                options={ENTITY_TYPES.map(([value, label]) => ({ value, label }))} />
                        <Input label="State of formation" value={f.state_of_formation || ''} onChange={set('state_of_formation')} />
                        <Input label="Years in business" type="number" step="any" value={f.years_in_business || ''}
                               onChange={set('years_in_business')}
                               hint={criteria?.min_years_in_business ? `${criteria.min_years_in_business} minimum` : ''} />
                        <Input label="Employees" type="number" value={f.employees || ''} onChange={set('employees')} />
                        <Input label="NAICS" value={f.naics_code || ''} onChange={set('naics_code')} />
                    </div>
                    <Textarea label="Use of the premises — what will actually happen in the space" rows={2}
                              value={f.intended_use || ''} onChange={set('intended_use')} />
                </Section>

                <Section title="Business credit and references"
                         hint="A business score first. Personal credit only where the entity is thin, and only with written authorisation.">
                    <div className="grid gap-x-4 sm:grid-cols-3">
                        <Input label="PAYDEX" type="number" value={f.paydex_score || ''} onChange={set('paydex_score')}
                               hint={criteria?.min_paydex ? `${criteria.min_paydex} minimum` : ''} />
                        <Input label="Bureau" value={f.business_credit_bureau || ''} onChange={set('business_credit_bureau')} />
                        <Input label="Credit notes" value={f.business_credit_notes || ''} onChange={set('business_credit_notes')} />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <p className="mb-1 text-xs font-medium text-slate-600">Bank references</p>
                            <Rows rows={f.bank_references || []} onChange={setList('bank_references')} disabled={locked}
                                  blank={{ name: '', contact: '', result: '' }} addLabel="+ Add bank reference"
                                  render={refRow('Bank')} />
                        </div>
                        <div>
                            <p className="mb-1 text-xs font-medium text-slate-600">Trade / prior-landlord references</p>
                            <Rows rows={f.trade_references || []} onChange={setList('trade_references')} disabled={locked}
                                  blank={{ name: '', contact: '', result: '' }} addLabel="+ Add trade reference"
                                  render={refRow('Supplier / landlord')} />
                        </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                        <Check label="Bank reference verified" checked={f.bank_reference_checked} onChange={check('bank_reference_checked')} />
                    </div>
                </Section>

                <Section title="Business financials">
                    <div className="grid gap-x-4 sm:grid-cols-4">
                        <Input label="Annual revenue" type="number" value={f.annual_revenue || ''} onChange={set('annual_revenue')} />
                        <Input label="EBITDA" type="number" value={f.ebitda ?? ''} onChange={set('ebitda')} />
                        <Input label="Annual rent" type="number" value={f.annual_rent || ''} onChange={set('annual_rent')} />
                        <Input label="Current ratio" type="number" step="any" value={f.current_ratio || ''} onChange={set('current_ratio')} />
                        <Input label="Statement years" type="number" value={f.financial_statements_years || ''}
                               onChange={set('financial_statements_years')}
                               hint={criteria?.financial_statement_years ? `${criteria.financial_statement_years} required` : ''} />
                        <Select label="Statement quality" value={f.statements_audited || ''} onChange={set('statements_audited')}
                                options={[{ value: '', label: 'Select…' }, { value: 'audited', label: 'Audited' },
                                          { value: 'reviewed', label: 'Reviewed' }, { value: 'compiled', label: 'Compiled' },
                                          { value: 'internal', label: 'Internal' }, { value: 'none', label: 'None produced' }]} />
                    </div>
                    {coverage != null && (
                        <p className={`mt-2 rounded px-3 py-2 text-sm ${
                            coverageShort ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900'}`}>
                            Rent coverage <strong>{coverage}x</strong> — {money(ebitda)} EBITDA against {money(annualRent)} annual rent
                            {minCoverage != null && <> against a {minCoverage}x minimum</>}.
                            {coverageShort && ' The gap is covered by a guaranty, a letter of credit or a larger deposit, not by a refusal.'}
                        </p>
                    )}
                </Section>

                <Section title="Principals and guarantors — personal financials"
                         hint="Tick 'personal credit report pulled' for each person whose consumer report was obtained. If the application is declined relying on it, that person is owed an FCRA adverse action notice.">
                    <div className="mb-2 flex flex-wrap gap-4 text-sm text-slate-600">
                        <Check label="Personal credit report pulled on the principal" checked={f.principal_credit_pulled}
                               onChange={check('principal_credit_pulled')} />
                    </div>
                    {f.principal_credit_pulled && (
                        <div className="grid gap-x-4 sm:grid-cols-2">
                            <Input label="Principal name" value={f.principal_name || ''} onChange={set('principal_name')} />
                            <Input label="Personal score" type="number" value={f.principal_credit_score || ''}
                                   onChange={set('principal_credit_score')} />
                        </div>
                    )}
                    <Rows rows={f.guarantors || []} onChange={setList('guarantors')} disabled={locked}
                          blank={{ name: '', kind: 'personal', net_worth: '', liquid_assets: '', annual_income: '',
                                   credit_pulled: false, credit_score: '' }}
                          addLabel="+ Add guarantor"
                          render={(g, up) => (
                              <div>
                                  <div className="grid gap-x-2 sm:grid-cols-4">
                                      <Input label="Guarantor" value={g.name || ''} onChange={(e) => up('name', e.target.value)} />
                                      <Select label="Kind" value={g.kind || 'personal'} onChange={(e) => up('kind', e.target.value)}
                                              options={[{ value: 'personal', label: 'Personal' },
                                                        { value: 'corporate', label: 'Corporate (parent)' }]} />
                                      <Input label="Relationship / % owned" value={g.relationship || ''}
                                             onChange={(e) => up('relationship', e.target.value)} />
                                      <Input label="Net worth" type="number" value={g.net_worth || ''}
                                             onChange={(e) => up('net_worth', e.target.value)} />
                                      <Input label="Liquid assets" type="number" value={g.liquid_assets || ''}
                                             onChange={(e) => up('liquid_assets', e.target.value)} />
                                      <Input label="Annual income" type="number" value={g.annual_income || ''}
                                             onChange={(e) => up('annual_income', e.target.value)} />
                                      {g.kind !== 'corporate' && (
                                          <Input label="Credit score" type="number" value={g.credit_score || ''}
                                                 onChange={(e) => up('credit_score', e.target.value)} />
                                      )}
                                  </div>
                                  {g.kind !== 'corporate' && (
                                      <div className="mb-1 text-sm text-slate-600">
                                          <Check label="Personal credit report pulled (FCRA)" checked={g.credit_pulled}
                                                 onChange={(e) => up('credit_pulled', e.target.checked)} />
                                      </div>
                                  )}
                              </div>
                          )} />
                </Section>

                <Section title="What stands behind the lease">
                    <div className="grid gap-x-4 sm:grid-cols-4">
                        <Select label="Security offered" value={f.guaranty_offered || ''} onChange={set('guaranty_offered')}
                                options={[{ value: '', label: 'None offered' }, { value: 'personal', label: 'Personal guaranty' },
                                          { value: 'corporate', label: 'Corporate guaranty' },
                                          { value: 'letter_of_credit', label: 'Letter of credit' }]} />
                        <Input label="LC amount" type="number" value={f.letter_of_credit_amount || ''}
                               onChange={set('letter_of_credit_amount')} />
                        <Input label="Deposit (months)" type="number" step="any" value={f.security_deposit_months || ''}
                               onChange={set('security_deposit_months')}
                               hint={criteria?.security_deposit_months ? `${criteria.security_deposit_months} standard` : ''} />
                        <Input label="Liability limit" type="number" value={f.insurance_liability_limit || ''}
                               onChange={set('insurance_liability_limit')}
                               hint={criteria?.insurance_min_liability ? money(criteria.insurance_min_liability) + ' minimum' : ''} />
                    </div>
                </Section>

                {industrial && (
                    <Section title="Environmental, power and loading"
                             hint="These outlive the lease. CERCLA reaches the owner regardless of who spilled it, so the answers belong on file before signature.">
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            <Check label="Handles hazardous materials" checked={f.handles_hazardous_materials}
                                   onChange={check('handles_hazardous_materials')} />
                            <Check label="Phase I on file" checked={f.phase_i_esa_on_file} onChange={check('phase_i_esa_on_file')} />
                            <Check label="Outdoor storage requested" checked={f.outdoor_storage_requested}
                                   onChange={check('outdoor_storage_requested')} />
                            <Check label="Use verified against zoning" checked={f.zoning_use_verified}
                                   onChange={check('zoning_use_verified')} />
                        </div>
                        <div className="mt-2 space-y-2">
                            <Textarea label="Processes — welding, plating, spray booth, food, assembly…" rows={2}
                                      value={f.processes_detail || ''} onChange={set('processes_detail')} />
                            {f.handles_hazardous_materials && (
                                <>
                                    <Textarea label="Materials handled, itemised" rows={2}
                                              value={f.hazardous_materials_detail || ''}
                                              onChange={set('hazardous_materials_detail')} />
                                    <Input label="Environmental permits held" value={f.environmental_permits || ''}
                                           onChange={set('environmental_permits')} />
                                </>
                            )}
                            <div className="grid gap-x-4 sm:grid-cols-2">
                                <Input label="Phase I date" type="date" value={f.phase_i_esa_date || ''}
                                       onChange={set('phase_i_esa_date')} />
                                <Input label="Zoning note" value={f.zoning_note || ''} onChange={set('zoning_note')} />
                                <Input label="Floor load required" value={f.floor_load_required || ''}
                                       onChange={set('floor_load_required')} />
                                <Input label="Power required" value={f.power_required || ''} onChange={set('power_required')} />
                                <Input label="Loading — docks, drive-ins, trailer parking" className="sm:col-span-2"
                                       value={f.loading_requirements || ''} onChange={set('loading_requirements')} />
                            </div>
                        </div>
                    </Section>
                )}

                <Textarea label="Underwriting notes" rows={2} value={f.notes || ''} onChange={set('notes')} />
            </fieldset>

            {!locked && (
                <Button disabled={busy} onClick={submit}>
                    {business ? 'Re-run underwriting' : 'Run underwriting'}
                </Button>
            )}

            {shown && <UnderwritingResult result={shown} environmental={shown.environmental}
                                          criteriaVersion={shown.criteria_version || application?.criteria_version} />}
        </div>
    );
};

export default BusinessUnderwriting;
